/**
 * Client-side helpers for the landing page chatbot widget: analytics event
 * names, the client request timeout, and the map from an API error to the
 * message the visitor sees.
 */

/** Analytics event fired when a visitor opens the chat widget. */
export const CHAT_OPENED_EVENT = "landing_page_chat_opened";

/** Analytics event fired when a visitor submits a question. */
export const CHAT_QUESTION_SUBMITTED_EVENT = "landing_page_chat_question_submitted";

/** Analytics event fired when the assistant returns an answer (carries latency). */
export const CHAT_ANSWER_RECEIVED_EVENT = "landing_page_chat_answer_received";

/** Analytics event fired when a question fails (carries the status code). */
export const CHAT_ERROR_EVENT = "landing_page_chat_error";

/**
 * Client request timeout in milliseconds. Kept below the route's
 * `maxDuration = 60` so the widget aborts and shows a retry before the server
 * connection is cut, instead of leaving the typing dots up with no way to cancel.
 */
export const CHAT_REQUEST_TIMEOUT_MS = 45_000;

/** What went wrong with a chatbot request, resolved from the API response or the fetch failure. */
export interface ChatErrorContext {
  /** HTTP status code, or null for a network failure or client abort. */
  status: number | null;
  /** The API's `error` field, when the response carried one. */
  errorCode?: string;
  /** True when the client aborted the request on its own timeout. */
  timedOut?: boolean;
}

/** A visitor-facing error message and whether retrying the same question can help. */
export interface ChatErrorCopy {
  /** The sentence shown in the failed assistant bubble. */
  copy: string;
  /** True when a retry button should appear (transient failures only). */
  retryable: boolean;
}

const TROUBLE_CONNECTING: ChatErrorCopy = {
  copy: "I'm having trouble connecting right now. Please try again in a moment.",
  retryable: true,
};

/**
 * Maps a failed chatbot request to the message the visitor sees. The API returns
 * eleven distinct error cases; this turns the ones a visitor can act on into
 * specific copy (rate limit, sign-in, password, message too long) and keeps the
 * generic "trouble connecting" line only for genuine network or server failures.
 *
 * @param context - The status, API error field, and timeout flag for the failure.
 * @returns The visitor-facing copy and whether a retry button should show.
 */
export function resolveChatErrorCopy(context: ChatErrorContext): ChatErrorCopy {
  const { status, errorCode, timedOut } = context;

  if (timedOut) {
    return { copy: "That is taking longer than expected. Please try again.", retryable: true };
  }

  if (status === 429) {
    return { copy: "The assistant is busy right now. Please wait a moment and try again.", retryable: true };
  }

  if (status === 401 && errorCode === "Password required") {
    return {
      copy: "This page is password protected. Please re-enter the page password, then ask again.",
      retryable: false,
    };
  }

  if (status === 401) {
    return { copy: "Please sign in to use the assistant on this page.", retryable: false };
  }

  if (status === 403) {
    return { copy: "The assistant is not available on this page.", retryable: false };
  }

  if (status === 400 && errorCode === "Message too long") {
    return { copy: "Your question is too long. Please shorten it and try again.", retryable: false };
  }

  // Network failure, 404, 5xx, and any other status all read as a transient
  // connection problem the visitor can retry.
  return TROUBLE_CONNECTING;
}
